import { SkapiError } from "skapi-js";
import { ref } from "vue";
import router from "@/router";
import { fullScreenLoader, removeFullScreenLoader } from "@/components/jsComponents";
import { user } from "@/code/user";
import { track } from "@/code/analytics";
import { skapi } from "@/main";
import { beginMcpOAuthOnLogin, MCP_BASE_URL } from "@/code/mcp_oauth";

async function getSkapi() {
    const main = await import("@/main");
    return main.skapi;
}

function getRawQueryParam(name: string): string | null {
    const match = window.location.search.match(new RegExp(`[?&]${name}=([^&]+)`));
    return match ? decodeURIComponent(match[1]) : null;
}

const urlParams = new URLSearchParams(window.location.search);
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID; // Replace this with your actual client ID
export const openIdLoginError = ref<string | null>(null);

const GOOGLE_OAUTH_IN_PROGRESS_KEY = "google_oauth_in_progress";
const GOOGLE_OAUTH_REDIRECT_URI_KEY = "google_oauth_redirect_uri";

function clearGoogleOAuthStart() {
    window.sessionStorage.removeItem(GOOGLE_OAUTH_IN_PROGRESS_KEY);
    window.sessionStorage.removeItem(GOOGLE_OAUTH_REDIRECT_URI_KEY);
}

// Read the inbound OAuth params (e.g. from MCP/chatgpt) that were stashed in
// sessionStorage by login.vue's onLoginHanger under "oauth:<state>". Removes
// the entry once read. Returns null if absent or unparseable.
function readStashedInboundOAuth(state: string): any | null {
    const key = "oauth:" + state;
    const stored = window.sessionStorage.getItem(key);
    if (!stored) return null;
    window.sessionStorage.removeItem(key);
    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

// The MCP server is the ONLY host allowed to receive a session-bearing
// callback. It always sends its own fixed /oauth/callback as the inbound
// redirect_uri (skapi-mcp/oauth.js builds that from OAUTH_CALLBACK_URI, never
// from the calling platform's query), so any other value on that parameter is
// forged. Without this check a crafted link of the form
//   /login?oauth=platform&state=x&redirect_uri=https://attacker.example/
// hands a signed-in visitor's entire skapi session to whoever authored it:
// access and id tokens (1 day) plus a refresh token that the admin user pool
// issues with a ten-year validity. One click, no consent screen, no symptom.
const isAllowedOAuthRedirect = (uri: string): boolean => {
    try {
        // Resolved against our own origin so a relative or scheme-less value
        // cannot slip through as "same origin, therefore fine".
        return new URL(uri, window.location.origin).origin === new URL(MCP_BASE_URL).origin;
    } catch {
        return false;
    }
};

/**
 * Package the skapi session as a `code` for the MCP server's OAuth callback.
 *
 * Returns null when there is no legitimate destination: no redirect_uri was
 * stashed, or the one that was does not belong to the MCP server. Callers MUST
 * treat null as "stay put and show the form" rather than navigating, because
 * assigning an empty string to window.location.href reloads the current URL
 * and turns a refused callback into a redirect loop.
 */
export function genOAuthCallbackUrl(oauthState: string, session: any, oauthParams?: any): string | null {
    const oauthRedirectUri: string = (oauthParams && oauthParams.redirect_uri) || "";

    if (!oauthRedirectUri || !isAllowedOAuthRedirect(oauthRedirectUri)) {
        console.error(
            "Refusing to return an OAuth code to an untrusted redirect_uri:",
            oauthRedirectUri,
        );
        return null;
    }

    const code = {
        access_token: session.accessToken.jwtToken,
        refresh_token: session.refreshToken.token,
        id_token: session.idToken.jwtToken,
    };

    // Base64 encode to make it URL-safe
    const encodedCode = btoa(JSON.stringify(code));

    return oauthRedirectUri +
        (oauthRedirectUri.includes("?") ? "&" : "?") +
        "code=" + encodeURIComponent(encodedCode) +
        "&state=" + encodeURIComponent(oauthState);
}

/** `page` is where the button was pressed: "login" or "signup". */
export function googleLogin(e: Event, page: string, useExistingState = false) {
    e.preventDefault();
    openIdLoginError.value = null;

    let url_split = window.location.href.split("?");
    let REDIRECT_URL = url_split[0];
    let rnd;
    
    if (useExistingState) {
        // If useExistingState is true, try to reuse the existing state parameter from the URL
        const urlParams = new URLSearchParams(window.location.search);
        // If the state parameter exists, use it; otherwise, throw error
        if (!urlParams.get("state")) {
            console.error("No state parameter found in URL to reuse.");
            alert("Error: No state parameter found in URL to reuse.");
            return;
        }
        rnd = urlParams.get("state");
    } else {
        rnd = Math.random().toString(36).substring(2); // Generate a random string
    }

    // console.log("Redirect URL:", REDIRECT_URL);
    // Build link to login page
    let url = "https://accounts.google.com/o/oauth2/v2/auth";
    url += "?client_id=" + GOOGLE_CLIENT_ID;
    url += "&redirect_uri=" + encodeURIComponent(REDIRECT_URL);
    url += "&response_type=code";
    url +=
        "&scope=" +
        encodeURIComponent(
            "https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email",
        );
    url += "&prompt=consent";
    url += "&state=" + encodeURIComponent(rnd);
    url += "&access_type=offline";

    window.sessionStorage.setItem(GOOGLE_OAUTH_IN_PROGRESS_KEY, "1");
    window.sessionStorage.setItem(GOOGLE_OAUTH_REDIRECT_URI_KEY, REDIRECT_URL);

    // The event is reported first and the redirect follows it. track() runs the
    // redirect as soon as the event has left, after a short wait at most, and at
    // once when analytics is not running, so the button never waits on the tag.
    track("google_continue", { target: page }, () => {
        window.location.href = url;
    });
}

// console.log("Google Client ID:", GOOGLE_CLIENT_ID);

/**
 * Same test handleGoogleOAuthReturn() applies below, exposed so a view can tell
 * BEFORE it paints that this page load is Google handing the user back rather
 * than someone arriving to sign in. Purely synchronous (URL + sessionStorage).
 */
export const isGoogleOAuthReturn = (): boolean =>
    !!urlParams.get("code") &&
    window.sessionStorage.getItem(GOOGLE_OAUTH_IN_PROGRESS_KEY) === "1";

/**
 * OUTBOUND: this app returning from Google's authorization endpoint.
 *
 * Triggered when Google redirected the browser back here with `?code=...`
 * AND we previously set `google_oauth_in_progress=1` in sessionStorage from
 * `googleLogin()`. We exchange the code for an access token via skapi's
 * `forwardRequest`, sign the user in via `skapi.openIdLogin`, optionally
 * merge a pre-existing skapi account, then redirect either to the IdP caller
 * (when `state` is present) or to `/my-projects`.
 *
 * @returns 'redirecting' if the browser is leaving, otherwise undefined.
 */
export const handleGoogleOAuthReturn = async (): Promise<'redirecting' | void> => {
    if (!urlParams.get("code")) return;

    const oauthInProgress = window.sessionStorage.getItem(GOOGLE_OAUTH_IN_PROGRESS_KEY) === "1";
    if (!oauthInProgress) return;

    // the webpage is loaded, and it's redirected from the Google login page.
        fullScreenLoader();
        const skapi = await getSkapi();

        let REDIRECT_URL = window.sessionStorage.getItem(GOOGLE_OAUTH_REDIRECT_URI_KEY) || window.location.href.split("?")[0]; // Must match redirect_uri used when login started
        const code = getRawQueryParam("code") || urlParams.get("code");

        function removeLoader() {
            removeFullScreenLoader();

            // remove get parameters from URL
            const newUrl = window.location.pathname;

            // Use router to replace URL without reloading
            router.replace(newUrl);
        }

        // Safely retrieve access token using forwardRequest
        const csrData = skapi.forwardRequest(null, {
            secretName: "ggl",
            url: "https://oauth2.googleapis.com/token",
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            data: {
                code: code,
                client_id: GOOGLE_CLIENT_ID,
                client_secret: "$CLIENT_SECRET",
                redirect_uri: REDIRECT_URL,
                grant_type: "authorization_code",
            },
        });

        let data = await csrData.catch((err) => {
            if (!(err instanceof SkapiError)) {
                removeLoader();
                throw err;
            }

            // INVALID_REQUEST는 forwardRequest 에러
            if (err?.code === "INVALID_REQUEST") {
                openIdLoginError.value =
                    err.message || "Invalid request for Google login.";
                removeLoader();
                return;
            }

            if (err.message.includes("NOT_EXISTS")) {
                openIdLoginError.value = "The account does not exist.";
                removeLoader();
                return;
            }

            // openIdLogin에서 throw된 에러면 이미 메시지 설정됨, 글로벌 핸들러로 전파
            // forwardRequest의 예상치 못한 에러는 메시지 설정 후 전파
            if (!openIdLoginError.value) {
                openIdLoginError.value =
                    "Google login failed. Please try again.";
                removeLoader();
            }

            throw err;
        });

        // data.then(async (data: any) => {
        // await router.isReady();

        // The catch above already showed the message and removed the loader.
        if (!data) {
            clearGoogleOAuthStart();
            return;
        }

        if (data.error) {
            if (import.meta.env.DEV) {
                console.error(data);
            }

            // Google OAuth error 처리
            let errorData = data.error;
            if (typeof errorData === "string") {
                try {
                    errorData = JSON.parse(errorData);
                } catch (e) {
                    errorData = { error: data.error };
                }
            }

            switch (errorData.error) {
                case "invalid_grant":
                    openIdLoginError.value =
                        "Google login session expired. Please try again.";
                    break;
                case "access_denied":
                    openIdLoginError.value = "Google login was denied.";
                    break;
                default:
                    openIdLoginError.value =
                        "Google login failed. Please try again.";
            }

            clearGoogleOAuthStart();
            removeLoader();
            return;
        }

        openIdLoginError.value = null;

        // Use openIdLogin to log in
        try {
            await skapi.openIdLogin({
                id: "by_skapi",
                token: data.access_token,
            });

            // Bounce back to the calling platform ONLY when this Google
            // round-trip was part of an inbound hand-off, which is exactly
            // when a stash exists under "oauth:<state>". googleLogin() always
            // attaches a state of its own, so keying off `state` alone sent
            // every ordinary Google sign-in to a relative "?code=<session>"
            // URL: the whole skapi session landed in the address bar and
            // browser history, and the MCP bootstrap below never ran.
            const inboundState = urlParams.get("state");
            const stashed = inboundState ? readStashedInboundOAuth(inboundState) : null;
            if (inboundState && stashed) {
                const callbackUrl = genOAuthCallbackUrl(inboundState, skapi.session, stashed);
                if (callbackUrl) {
                    clearGoogleOAuthStart();
                    window.location.href = callbackUrl;
                    return 'redirecting';
                }
                // Refused (untrusted redirect_uri): fall through and treat this
                // as an ordinary sign-in rather than navigating anywhere.
            }

            clearGoogleOAuthStart();
            // Same as the email/password login path: register an MCP OAuth
            // client and start the authorization-code flow before landing
            // on /my-projects. Browser is leaving the SPA.
            try {
                await beginMcpOAuthOnLogin("/my-projects");
                return 'redirecting';
            } catch (err) {
                console.error("MCP OAuth bootstrap failed", err);
                // Fall through so the user isn't stranded if MCP is down.
            }
            window.location.href = "/my-projects";
            return 'redirecting';
        } catch (err: any) {
            if (!(err instanceof SkapiError)) throw err;

            if (err.code === "ACCOUNT_EXISTS") {
                let merge = confirm(
                    "An account with this Google account already exists.\nWould you like to merge accounts?\nOnce merged, you cannot login from the previous method.",
                );
                if (!merge) {
                    // Declined: nobody is signed in, so there is nowhere to go.
                    // Stay here and let the sign-in form show.
                    clearGoogleOAuthStart();
                    removeLoader();
                    return;
                }

                // The retry can fail like the first attempt did (the backend
                // answers ACCOUNT_EXISTS again when it cannot take the account
                // over). Nothing below runs then, so without this the
                // full-screen loader would stay up for good.
                try {
                    await skapi.openIdLogin({
                        id: "by_skapi",
                        token: data.access_token,
                        merge: ["name"],
                    });
                } catch (mergeErr: any) {
                    console.error("Google account merge failed", mergeErr);
                    openIdLoginError.value =
                        mergeErr?.code === "ACCOUNT_EXISTS"
                            ? "The Google sign-in could not be completed for this account. Please try again or contact support."
                            : "Login failed. Please try again.";
                    clearGoogleOAuthStart();
                    removeLoader();
                    return;
                }

                const mergedState = urlParams.get("state");
                const mergedStash = mergedState ? readStashedInboundOAuth(mergedState) : null;
                if (mergedState && mergedStash) {
                    const callbackUrl = genOAuthCallbackUrl(mergedState, skapi.session, mergedStash);
                    if (callbackUrl) {
                        clearGoogleOAuthStart();
                        window.location.href = callbackUrl;
                        return 'redirecting';
                    }
                }
                clearGoogleOAuthStart();
                // Same as the non-merge path: kick off MCP OAuth before /my-projects.
                try {
                    await beginMcpOAuthOnLogin("/my-projects");
                    return 'redirecting';
                } catch (err) {
                    console.error("MCP OAuth bootstrap failed", err);
                }
                window.location.href = "/my-projects";
                return 'redirecting';
            }

            if (
                err.message === "Account needs to be confirmed." ||
                err.message === "The account needs to be confirmed."
            ) {
                openIdLoginError.value =
                    "Your account needs to be confirmed. Please check your email for the confirmation link.";
                clearGoogleOAuthStart();
                removeLoader();
                return;
            }

            if (err.message.includes("NOT_EXISTS")) {
                openIdLoginError.value = "The account does not exist.";
                clearGoogleOAuthStart();
                removeLoader();
                return;
            }

            // 처리되지 않은 openIdLogin 에러
            openIdLoginError.value = "Login failed. Please try again.";
            clearGoogleOAuthStart();
            removeLoader();

            throw err;
        }
};